import * as React from "react";
import {useEffect, useState} from "react";
import {Button, Dropdown, Form, Icon, Input, Message, Segment, TextArea} from "semantic-ui-react";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {Address, Company, ParseTextRequest} from "../../../../../generated";
import {CompanyArchiveModal} from "./CompanyArchiveModal";
import {CompanyAddressEditModal} from "./CompanyAddressEditModal";
import Notifications, {notify} from 'react-notify-toast';
import {ConfigContext} from "../../../context/ConfigContext";


export interface CompanyEditPageProps {
}

export function CompanyEditPage(props: React.PropsWithChildren<CompanyEditPageProps>) {

    const context = React.useContext(ConfigContext)

    const auth = backend.withTokenAuthHeader(authentication.token)
    const [company, setCompany] = useState<Company | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isResetting, setIsResetting] = useState(false)
    const [aiIsLoading, setAiIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [showArchiveModal, setShowArchiveModal] = useState(false)
    const [showAddressEditModal, setShowAddressEditModal] = useState(false)

    // Form fields
    const [name, setName] = useState("")
    const [tag, setTag] = useState("")
    const [address, setAddress] = useState("")
    const [addressObj, setAddressObj] = useState<Address | null>(null)
    const [lifecycleStatus, setLifecycleStatus] = useState<Company.LifecycleStatusEnum | undefined>(undefined)
    const [lifecycleStatusDescription, setLifecycleStatusDescription] = useState("")
    const [lifecycleStatusProductionDueDate, setLifecycleStatusProductionDueDate] = useState<string>("")

    useEffect(() => {
        loadCompany()
    }, [context.companyId]);

    async function loadCompany() {
        try {
            setIsLoading(true)
            setError(null)
            const result = await backend.companyApi.fetchCompanyById(context.companyId, auth)
            setCompany(result)
            setName(result.name || "")
            setTag(result.tag || "")
            setAddress(addressToString(result.address) ?? "")
            setAddressObj(result.address || null)
            setLifecycleStatus(result.lifecycleStatus)
            setLifecycleStatusDescription(result.lifecycleStatusDescription ?? "")
            setLifecycleStatusProductionDueDate(
                result.lifecycleStatusProductionDueDate 
                    ? new Date(result.lifecycleStatusProductionDueDate).toISOString().split('T')[0] 
                    : ""
            )
        } catch (err: any) {
            console.error("Error loading company:", err)
            setError(err.message || "Failed to load company data")
            notify.show("ERROR: Failed to load company data", 'error', 5000, '#fc0303')
        } finally {
            setIsLoading(false)
        }
    }

    async function reset() {
        setIsResetting(true)
        setError(null)
        await loadCompany()
        setIsResetting(false)
    }

    function addressToString(address: Address | null | undefined): string {
        if (address) {
            let str = address.name + "\n"
            if (address.additionalInfo) {
                str = str + address.additionalInfo + "\n"
            }
            str = str + address.street + " " + address.houseNumber + "\n" +
                address.zipCode + " " + address.city + "\n" +
                address.country
            return str
        }
        return ""
    }

    async function addressToObject() {
        setAiIsLoading(true)
        try {
            let input = "Input | Output as JSON |\n" +
                "Mercedes Benz AG, Stuttgarter Str. 14, 72622, Nürtingen | {\"name\":\"Mercedes Benz AG\", \"city\": \"Nürtingen\", \"street\":\"Stuttgarter Str.\", \"houseNumber\": \"14\", \"zipCode\": \"72622\", \"country\": \"DE\"} |\n" +
                "Edeka Minden-Hannover, EDEKA-LAUE Peugeotstraße 1, D-31867 Lauenau | {\"name\":\"Edeka Minden-Hannover\", \"city\": \"Lauenau\", \"street\":\"Peugeotstraße\", \"houseNumber\": \"1\", \"zipCode\": \"31867\", \"country\": \"DE\"} |\n" +
                replaceAll(replaceAll(address, "\n", ", "), "|", ", ") + "|"

            let aiRequest = {
                input: input,
                rawInput: address,
                type: "address",
                stopSequence: "|"
            } as ParseTextRequest
            let aiResponse = await backend.internalApi.aiParseText(aiRequest, auth)
            setAddressObj(JSON.parse(aiResponse.output))
        } catch (err: any) {
            console.error("Error parsing address:", err)
            notify.show("ERROR: Failed to parse address", 'error', 5000, '#fc0303')
        } finally {
            setAiIsLoading(false)
        }
    }

    function replaceAll(str: string, find: string, replace: string): string {
        return str.replace(new RegExp(find, 'g'), replace);
    }

    async function save() {
        if (!company) return

        setIsSaving(true)
        setError(null)

        try {
            let request = await backend.companyApi.fetchCompanyById(authentication.company._id, auth)

            // Update only changed fields
            if (name !== company.name) {
                request.name = name
            }
            if (tag !== company.tag) {
                request.tag = tag
            }
            if (addressObj !== company.address) {
                request.address = addressObj || undefined
            }
            if (lifecycleStatus !== company.lifecycleStatus) {
                request.lifecycleStatus = lifecycleStatus
            }
            if (lifecycleStatusDescription !== (company.lifecycleStatusDescription ?? "")) {
                request.lifecycleStatusDescription = lifecycleStatusDescription || undefined
            }
            const currentDueDate = company.lifecycleStatusProductionDueDate 
                ? new Date(company.lifecycleStatusProductionDueDate).toISOString().split('T')[0] 
                : ""
            if (lifecycleStatusProductionDueDate !== currentDueDate) {
                request.lifecycleStatusProductionDueDate = lifecycleStatusProductionDueDate 
                    ? new Date(lifecycleStatusProductionDueDate + 'T00:00:00Z') 
                    : undefined
            }

            const currentCompany = await backend.companyApi.updateCompany(context.companyId, request, auth)

            authentication.company = currentCompany
            setCompany(currentCompany)
            
            // Update local state with saved values
            setName(currentCompany.name || "")
            setTag(currentCompany.tag || "")
            setAddress(addressToString(currentCompany.address) ?? "")
            setAddressObj(currentCompany.address || null)
            setLifecycleStatus(currentCompany.lifecycleStatus)
            setLifecycleStatusDescription(currentCompany.lifecycleStatusDescription ?? "")
            setLifecycleStatusProductionDueDate(
                currentCompany.lifecycleStatusProductionDueDate 
                    ? new Date(currentCompany.lifecycleStatusProductionDueDate).toISOString().split('T')[0] 
                    : ""
            )

            notify.show('Company updated successfully', 'success', 3000, '#28f751');
        } catch (err: any) {
            console.error("Error saving company:", err)
            const errorMessage = err.response?.data?.message || err.message || "Failed to save company"
            setError(errorMessage)
            notify.show(`ERROR: ${errorMessage}`, 'error', 5000, '#fc0303')
        } finally {
            setIsSaving(false)
        }
    }

    function hasChanges(): boolean {
        if (!company) return false
        
        return (
            name !== company.name ||
            tag !== company.tag ||
            addressObj !== company.address ||
            lifecycleStatus !== company.lifecycleStatus ||
            lifecycleStatusDescription !== (company.lifecycleStatusDescription ?? "") ||
            lifecycleStatusProductionDueDate !== (company.lifecycleStatusProductionDueDate 
                ? new Date(company.lifecycleStatusProductionDueDate).toISOString().split('T')[0] 
                : "")
        )
    }

    if (isLoading) {
        return (
            <Segment loading>
                <p>Loading company data...</p>
            </Segment>
        )
    }

    if (!company) {
        return (
            <Message negative>
                <Message.Header>Error</Message.Header>
                <p>Company not found</p>
            </Message>
        )
    }

    const lifecycleStatusOptions = [
        { key: 'PRODUCTION', text: 'PRODUCTION', value: 'PRODUCTION' },
        { key: 'SETUP', text: 'SETUP', value: 'SETUP' },
        { key: 'TESTING', text: 'TESTING', value: 'TESTING' },
        { key: '', text: '(None)', value: undefined }
    ]

    return (
        <div style={{maxWidth: '800px', margin: '0 auto', padding: '20px'}}>
            <h2>Edit Company</h2>
            
            {error && (
                <Message negative onDismiss={() => setError(null)}>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <Form>
                <Form.Field>
                    <label>Company Name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Company name"
                    />
                </Form.Field>

                <Form.Field>
                    <label>Tag</label>
                    <Input
                        value={tag}
                        onChange={(e) => setTag(e.target.value)}
                        placeholder="Company tag"
                    />
                </Form.Field>

                <Form.Field>
                    <label>Address</label>
                    <div style={{display: 'flex', gap: '10px', alignItems: 'flex-start'}}>
                        <TextArea
                            rows={5}
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Company address"
                            style={{flex: 1}}
                        />
                        <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                            <Button
                                icon
                                loading={aiIsLoading}
                                onClick={addressToObject}
                                disabled={address === addressToString(addressObj)}
                                title="Parse address with AI"
                            >
                                <Icon name='microchip'/>
                            </Button>
                            <Button
                                icon
                                onClick={() => setShowAddressEditModal(true)}
                                title="Edit address in detailed form"
                            >
                                <Icon name='list'/>
                            </Button>
                        </div>
                    </div>
                    {address !== addressToString(addressObj) && (
                        <Message warning size="small" style={{marginTop: '5px'}}>
                            Address text doesn't match parsed address. Use AI parser or detailed form to update.
                        </Message>
                    )}
                </Form.Field>

                <Form.Field>
                    <label>Lifecycle Status</label>
                    <Dropdown
                        placeholder="Select lifecycle status"
                        selection
                        clearable
                        options={lifecycleStatusOptions}
                        value={lifecycleStatus || null}
                        onChange={(e, { value }) => setLifecycleStatus(value as Company.LifecycleStatusEnum | undefined)}
                    />
                </Form.Field>

                {lifecycleStatus && (
                    <>
                        <Form.Field>
                            <label>Lifecycle Description</label>
                            <TextArea
                                rows={3}
                                placeholder="Description of planned company project layout (e.g., 'Company is importing PDF and sending to TOM')"
                                value={lifecycleStatusDescription}
                                onChange={(e) => setLifecycleStatusDescription(e.target.value)}
                            />
                        </Form.Field>

                        {lifecycleStatus === 'SETUP' && (
                            <Form.Field>
                                <label>Production Due Date</label>
                                <Input
                                    type="date"
                                    value={lifecycleStatusProductionDueDate}
                                    onChange={(e) => setLifecycleStatusProductionDueDate(e.target.value)}
                                    placeholder="Due date for transition to PRODUCTION"
                                />
                            </Form.Field>
                        )}
                    </>
                )}

                <Form.Field>
                    <label>Archive Company</label>
                    <Button
                        icon
                        onClick={() => setShowArchiveModal(true)}
                        color="red"
                    >
                        <Icon name='archive'/>
                        Archive Company
                    </Button>
                </Form.Field>

                <div style={{marginTop: '20px', display: 'flex', gap: '10px'}}>
                    <Button
                        primary
                        disabled={!hasChanges() || isSaving}
                        loading={isSaving}
                        onClick={save}
                        icon
                        labelPosition="left"
                    >
                        <Icon name='save'/>
                        Save Changes
                    </Button>

                    <Button
                        disabled={!hasChanges() || isResetting}
                        loading={isResetting}
                        onClick={reset}
                        icon
                        labelPosition="left"
                    >
                        <Icon name='repeat'/>
                        Reset
                    </Button>
                </div>
            </Form>

            <CompanyArchiveModal
                isOpen={showArchiveModal}
                onClose={(success) => {
                    setShowArchiveModal(false)
                    if (success === true) {
                        notify.show('Company archived successfully', 'success', 3000, '#28f751');
                        loadCompany()
                    } else if (success === false) {
                        notify.show("ERROR: Unable to archive company.", 'error', 5000, '#fc0303')
                    }
                }}
            />

            {showAddressEditModal && (
                <CompanyAddressEditModal
                    isOpen={showAddressEditModal}
                    onClose={(newAddress) => {
                        setShowAddressEditModal(false)
                        if (newAddress) {
                            setAddressObj(newAddress)
                            setAddress(addressToString(newAddress))
                            notify.show('Address updated', 'success', 3000, '#28f751');
                        }
                    }}
                    address={addressObj}
                />
            )}
            
            <Notifications/>
        </div>
    )
}
